import ConfirmModal from "@/src/components/action-modals/confirm-modal";
import PersonalDetailsSection from "@/src/components/profile/profile-details";
import RoleSection from "@/src/components/profile/user-role";
import WorkDetailsSection from "@/src/components/profile/work-details";
import ScButton from "@/src/components/ui-kit/button";
import HeaderBar from "@/src/components/ui-kit/header-bar";
import { useAuth } from "@/src/contexts/auth.context";
import { useUploadFile } from "@/src/hooks/features/use-upload-file";
import {
  EditProfileFormValues,
  EditProfileRole,
} from "@/src/models/user.model";
import {
  useGetUserProfileQuery,
  useInsertUserProfileMutation,
  useUpdateUserProfileMutation,
} from "@/src/services/profile/profile.hooks";
import {
  useBlocksUserByIdQuery,
  useUpdateBlocksUserMutation,
} from "@/src/services/users.blocks.api";
import { parseDobString } from "@/src/utils/date.utils";
import { getOrgRoles } from "@/src/utils/roles";
import { pickMedia } from "@/src/utils/picker-utils";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ScrollView, View } from "react-native";

const EDIT_PROFILE_FORM_DEFAULTS: EditProfileFormValues = {
  firstName: "",
  lastName: "",
  phoneNumber: "",
  gender: "",
  description: "",
  address: "",
  country: "",
  designation: "",
  department: "",
  company: "",
  website: "",
  dob: null,
};

const EditProfileScreen = () => {
  // See ScInput: React Compiler must be off for this RHF form or initially-empty
  // fields reject typed input.
  "use no memo";
  const router = useRouter();
  const { t } = useTranslation();
  const { user: currentUser, isSuperAdmin, appRole, orgId } = useAuth();

  const { id, onboarding } = useLocalSearchParams();
  const singleId = Array.isArray(id) ? id[0] : id;
  const safeId = singleId ?? "";
  const isOnboarding = onboarding === "true";

  const isOwnProfile = currentUser?.itemId === singleId;
  // Own profile → edit personal/work details. Super-admin on someone else's
  // profile → edit their role only (the detail fields are hidden, not shown
  // disabled).
  const canEditFields = isOwnProfile;
  const canEditRole = isSuperAdmin && !isOwnProfile;

  const { data: blocksUser } = useBlocksUserByIdQuery(safeId, {
    enabled: !!singleId,
  });
  const { data: userProfile } = useGetUserProfileQuery(safeId);

  const { mutateAsync: uploadFileAsync } = useUploadFile();
  const { mutate: insertProfile, isPending: isInsertPending } =
    useInsertUserProfileMutation();
  const { mutate: updateProfile, isPending: isUpdatePending } =
    useUpdateUserProfileMutation();
  const { mutate: updateBlocksUser, isPending: isBlocksUserPending } =
    useUpdateBlocksUserMutation();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isDirty },
  } = useForm<EditProfileFormValues>({
    defaultValues: EDIT_PROFILE_FORM_DEFAULTS,
  });

  const [backgroundImageUrl, setBackgroundImageUrl] = useState("");
  const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState<
    "profile" | "background" | null
  >(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState<EditProfileRole[]>([]);

  useEffect(() => {
    if (!isOwnProfile && !isSuperAdmin) router.back();
  }, [isOwnProfile, isSuperAdmin, router]);

  useEffect(() => {
    if (blocksUser || userProfile) {
      reset({
        firstName: blocksUser?.firstName ?? "",
        lastName: blocksUser?.lastName ?? "",
        phoneNumber: blocksUser?.phoneNumber ?? "",
        gender: userProfile?.gender ?? "",
        description: userProfile?.description ?? "",
        address: userProfile?.address ?? "",
        country: userProfile?.country ?? "",
        designation: userProfile?.designation ?? "",
        department: userProfile?.department ?? "",
        company: userProfile?.company ?? "",
        website: userProfile?.website ?? "",
        dob: parseDobString(userProfile?.dob),
      });
      setProfileImageUrl(blocksUser?.profileImageUrl ?? null);
    }
    // Depend on the stable record IDs, not the query object references — react-query
    // can hand back a new object identity on refetch, which would re-run reset() and
    // wipe whatever the user has typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocksUser?.itemId, userProfile?.ItemId, reset]);

  useEffect(() => {
    if (userProfile)
      setBackgroundImageUrl(userProfile?.backgroundImageUrl ?? "");
  }, [userProfile]);

  useEffect(() => {
    if (blocksUser) {
      const orgRoles = getOrgRoles(blocksUser.memberships, orgId);
      setSelectedRoles(
        orgRoles.length ? (orgRoles as EditProfileRole[]) : ["member"],
      );
    }
  }, [blocksUser, orgId]);

  const toggleRole = (role: EditProfileRole) =>
    setSelectedRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role],
    );

  const isPending =
    isInsertPending ||
    isUpdatePending ||
    uploadingImage !== null ||
    isBlocksUserPending;
  const originalRoles = getOrgRoles(blocksUser?.memberships, orgId);
  const roleChanged =
    canEditRole &&
    (selectedRoles.length !== originalRoles.length ||
      selectedRoles.some((role) => !originalRoles.includes(role as any)));
  const profileImageChanged =
    profileImageUrl !== (blocksUser?.profileImageUrl ?? null);
  const backgroundImageChanged =
    backgroundImageUrl !== (userProfile?.backgroundImageUrl ?? "");
  // react-hook-form's isDirty compares Date objects as empty objects, so a dob
  // change alone never flips it — track the date explicitly by time value.
  const watchedDob = watch("dob");
  const originalDob = parseDobString(userProfile?.dob);
  const dobChanged =
    (watchedDob?.getTime() ?? null) !== (originalDob?.getTime() ?? null);
  const hasChanges =
    isDirty ||
    dobChanged ||
    profileImageChanged ||
    backgroundImageChanged ||
    roleChanged;

  const roleLabel =
    appRole === "super-admin"
      ? t("LABEL_SUPER_ADMIN")
      : appRole === "admin"
        ? t("LABEL_ADMIN")
        : t("LABEL_MEMBER");

  const handleCancel = () => router.back();

  const handlePickProfileImage = async () => {
    if (!canEditFields) return;
    const assets = await pickMedia();
    if (!assets || assets?.length === 0) return;
    const asset = assets[0];
    setUploadingImage("profile");
    try {
      const uploadedUrl = await uploadFileAsync({
        uri: asset?.uri,
        fileName: asset?.fileName ?? `profile_${Date.now()}.jpg`,
        mimeType: asset?.mimeType ?? "image/jpeg",
      });
      setProfileImageUrl(uploadedUrl?.split("?")[0]);
    } finally {
      setUploadingImage(null);
    }
  };

  const handlePickBackgroundImage = async () => {
    if (!canEditFields) return;
    const assets = await pickMedia();
    if (!assets || assets?.length === 0) return;
    const asset = assets[0];
    setUploadingImage("background");
    try {
      const uploadedUrl = await uploadFileAsync({
        uri: asset?.uri,
        fileName: asset?.fileName ?? `bg_${Date.now()}.jpg`,
        mimeType: asset?.mimeType ?? "image/jpeg",
      });
      const permanentUrl = uploadedUrl?.split("?")[0];
      setBackgroundImageUrl(permanentUrl);
      const profileInput = {
        gender: userProfile?.gender ?? "",
        description: userProfile?.description ?? "",
        address: userProfile?.address ?? "",
        country: userProfile?.country ?? "",
        designation: userProfile?.designation ?? "",
        department: userProfile?.department ?? "",
        company: userProfile?.company ?? "",
        website: userProfile?.website ?? "",
        backgroundImageUrl: permanentUrl,
        dob: userProfile?.dob ?? null,
      };

      if (userProfile) {
        updateProfile({ input: profileInput, userId: safeId });
      } else {
        insertProfile(profileInput);
      }
    } finally {
      setUploadingImage(null);
    }
  };
  const onSubmit = (formValues: EditProfileFormValues) => {
    // Super-admin editing another user: only the role is editable.
    if (canEditRole) {
      updateBlocksUser({
        userId: safeId,
        input: {
          firstName: blocksUser?.firstName ?? "",
          lastName: blocksUser?.lastName ?? "",
          phoneNumber: blocksUser?.phoneNumber ?? "",
          profileImageUrl: blocksUser?.profileImageUrl ?? null,
          roles: selectedRoles,
          orgId,
        },
      });
      setConfirmOpen(false);
      router.back();
      return;
    }

    if (!canEditFields) return;

    updateBlocksUser({
      userId: safeId,
      input: {
        firstName: formValues?.firstName,
        lastName: formValues?.lastName,
        phoneNumber: formValues?.phoneNumber,
        profileImageUrl,
        roles: undefined,
      },
    });
    const profileInput = {
      gender: formValues?.gender,
      description: formValues?.description,
      address: formValues?.address,
      country: formValues?.country,
      designation: formValues?.designation,
      department: formValues?.department,
      company: formValues?.company,
      website: formValues?.website,
      backgroundImageUrl,
      dob: formValues?.dob
        ? `${formValues.dob.getDate()}/<${String(formValues.dob.getMonth() + 1).padStart(2, "0")}>/${formValues.dob.getFullYear()}`
        : null,
    };

    const handleSuccess = () => {
      setConfirmOpen(false);
      reset(formValues);
      if (isOnboarding) router.replace("/feeds");
      else router.back();
    };

    if (userProfile) {
      updateProfile(
        { input: profileInput, userId: safeId },
        { onSuccess: handleSuccess },
      );
    } else {
      insertProfile(profileInput, { onSuccess: handleSuccess });
    }
  };

  if (!singleId) {
    router.back();
    return null;
  }

  return (
    <View className="flex-1 lg:px-12 bg-obsidian">
      <ScrollView
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
        automaticallyAdjustKeyboardInsets
        contentContainerStyle={{ paddingBottom: 80 }}
      >
        <View className="bg-chalk rounded-[16px] md:border md:border-ash-3 px-4 md:px-6 py-4 gap-6">
            <HeaderBar variant="topic" title="LABEL_EDIT_PROFILE" />

          {canEditFields && (
            <PersonalDetailsSection
              control={control}
              canEditFields={canEditFields}
              backgroundImageUrl={backgroundImageUrl}
              profileImageUrl={profileImageUrl}
              firstName={blocksUser?.firstName ?? undefined}
              lastName={blocksUser?.lastName ?? undefined}
              isUploadingProfile={uploadingImage === "profile"}
              isUploadingBackground={uploadingImage === "background"}
              onPickBackgroundImage={handlePickBackgroundImage}
              onPickProfileImage={handlePickProfileImage}
              genderAndDobInRow
            />
          )}

          {canEditFields && (
            <WorkDetailsSection control={control} canEditFields={canEditFields} />
          )}

          {canEditRole && (
            <View className="gap-2 md:border-t pt-4 md:border-ash-3">
              <RoleSection
                canEditRole={canEditRole}
                selectedRoles={selectedRoles}
                onToggleRole={toggleRole}
              />
            </View>
          )}

          {(canEditFields || canEditRole) && (
            <View className="flex-row justify-between pt-4">
              <ScButton
                text="BTN_CANCEL"
                mode="outlined"
                onPress={handleCancel}
                disabled={isPending}
              />
              <ScButton
                text="BTN_UPDATE"
                loading={isPending}
                onPress={handleSubmit(() => setConfirmOpen(true))}
                disabled={!hasChanges || isPending}
              />
            </View>
          )}
        </View>
      </ScrollView>

      <ConfirmModal
        visible={confirmOpen}
        title="QUEST_CHANGE_USER_DETAILS"
        description="LABEL_CHANGE_USER_DETAILS_DESC"
        confirmText="BTN_CONFIRM"
        cancelText="BTN_CANCEL"
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handleSubmit(onSubmit)}
      />
    </View>
  );
};

export default EditProfileScreen;
